import React, { useState, useEffect } from 'react';
import { productService } from '../services/productService';
import { paymentMethodService } from '../services/paymentMethodService';
import { salesService } from '../services/salesService';
import settingsService from '../services/settingsService';
import { customerService } from '../services/customerService';
import { Product, PaymentMethod, CartItem, SaleCreateDto, Sale, Customer } from '../types/types';
import { Receipt } from '../components/Receipt';
import {
  ShoppingCart,
  Search,
  Plus,
  Minus,
  Trash2,
  DollarSign,
  CheckCircle,
  X,
  Package,
} from 'lucide-react';
import './Sales.css';

const Sales: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number>(1);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastSaleId, setLastSaleId] = useState<number | null>(null);
  const [lastSale, setLastSale] = useState<Sale | null>(null);
  const [storeConfig, setStoreConfig] = useState<any>({});

  // Transfer details
  const [bankName, setBankName] = useState('');
  const [transferReference, setTransferReference] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsData, paymentMethodsData, settingsData, customersData] = await Promise.all([
        productService.getAllProducts(),
        paymentMethodService.getAllPaymentMethods(),
        settingsService.getSettings(),
        customerService.getAllCustomers()
      ]);
      setProducts(productsData.filter((p) => p.isActive));
      setPaymentMethods(paymentMethodsData);
      setStoreConfig(settingsData);
      setCustomers(customersData);
      if (paymentMethodsData.length > 0) {
        setSelectedPaymentMethod(paymentMethodsData[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: Product) => {
    const existingItem = cart.find((item) => item.product.id === product.id);

    if (existingItem) {
      if (existingItem.quantity < product.stock) {
        setCart(
          cart.map((item) =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        );
      } else {
        alert('Stock insuficiente');
      }
    } else {
      if (product.stock > 0) {
        setCart([...cart, { product, quantity: 1 }]);
      } else {
        alert('Producto sin stock');
      }
    }
  };

  const updateQuantity = (productId: number, delta: number) => {
    const item = cart.find((item) => item.product.id === productId);
    if (!item) return;

    const newQuantity = item.quantity + delta;

    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else if (newQuantity <= item.product.stock) {
      setCart(
        cart.map((item) =>
          item.product.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    } else {
      alert('Stock insuficiente');
    }
  };

  const removeFromCart = (productId: number) => {
    setCart(cart.filter((item) => item.product.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.product.price * item.quantity, 0);
  };

  const completeSale = async () => {
    if (cart.length === 0) {
      alert('El carrito está vacío');
      return;
    }

    // Transfer validation
    // Assuming ID 3 is Transfer based on standard seeding. Ideally check by name or attribute
    if (selectedPaymentMethod === 3) {
      if (!bankName.trim() || !transferReference.trim()) {
        alert('Por favor complete los datos de la transferencia');
        return;
      }
    }

    setProcessing(true);

    try {
      const saleData: SaleCreateDto = {
        items: cart.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
        })),
        paymentMethodId: selectedPaymentMethod,
        paymentAmount: calculateTotal(),
        customerId: selectedCustomerId,
        bankName: selectedPaymentMethod === 3 ? bankName : '',
        transferReference: selectedPaymentMethod === 3 ? transferReference : ''
      };

      const sale = await salesService.createSale(saleData);
      setLastSaleId(sale.id);
      setLastSale(sale); // Save full sale for receipt
      setShowSuccess(true);
      setCart([]);

      // Reset transfer fields
      setBankName('');
      setTransferReference('');
      setSelectedCustomerId(undefined);

      await loadData(); // Reload to update stock

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);

      // Trigger print dialog
      setTimeout(() => {
        window.print();
      }, 500);

    } catch (error: any) {
      console.error('Error completing sale:', error);
      alert(error.response?.data || 'Error al procesar la venta');
    } finally {
      setProcessing(false);
    }
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const total = calculateTotal();

  return (
    <div className="sales-container">
      {/* Printable Receipt */}
      {lastSale && (
          <div className="print-only">
            <Receipt sale={lastSale} storeConfig={storeConfig} />
          </div>
        )}

        {/* Products Section */}
        <div className="products-section">
          <div className="section-header">
            <h2>Productos</h2>
            <div className="search-box-sales">
              <Search size={20} />
              <input
                type="text"
                placeholder="Buscar por nombre o código..."
                value={searchTerm}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchTerm(val);
                  // Auto-add if exact barcode match
                  const product = products.find(p => p.barcode === val);
                  if (product) {
                    addToCart(product);
                    setSearchTerm(''); // Clear after adding
                  }
                }}
                className="search-input"
              />
            </div>
          </div>

          {loading ? (
            <div className="loading-state">Cargando productos...</div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="product-card"
                  onClick={() => addToCart(product)}
                >
                  <div className="product-image-container">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} className="product-img" />
                    ) : (
                      <div className="product-img-placeholder">
                        <Package size={32} />
                      </div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3 className="product-title">{product.name}</h3>
                    <p className="product-category">{product.category?.name}</p>
                    <div className="product-footer">
                      <span className="product-price">${product.price.toFixed(2)}</span>
                      <span className={`product-stock ${product.stock < 10 ? 'low' : ''}`}>
                        Stock: {product.stock}
                      </span>
                    </div>
                  </div>
                  <button className="add-btn">
                    <Plus size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Section */}
        <div className="cart-section">
          <div className="cart-header">
            <h2>
              <ShoppingCart size={24} />
              Carrito
            </h2>
            {cart.length > 0 && (
              <button className="clear-cart-btn" onClick={clearCart}>
                <X size={18} />
                Limpiar
              </button>
            )}
          </div>

          <div className="cart-items">
            {cart.length === 0 ? (
              <div className="empty-cart">
                <ShoppingCart size={48} />
                <p>Carrito vacío</p>
                <span>Selecciona productos para comenzar</span>
              </div>
            ) : (
              cart.map((item) => (
                <div key={item.product.id} className="cart-item">
                  <div className="cart-item-info">
                    <h4>{item.product.name}</h4>
                    <p className="cart-item-price">${item.product.price.toFixed(2)}</p>
                  </div>
                  <div className="cart-item-controls">
                    <button
                      className="qty-btn"
                      onClick={() => updateQuantity(item.product.id, -1)}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="qty-display">{item.quantity}</span>
                    <button
                      className="qty-btn"
                      onClick={() => updateQuantity(item.product.id, 1)}
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      className="remove-btn"
                      onClick={() => removeFromCart(item.product.id)}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <div className="cart-item-total">
                    ${(item.product.price * item.quantity).toFixed(2)}
                  </div>
                </div>
              ))
            )}
          </div>

          {cart.length > 0 && (
            <div className="cart-footer">
              <div className="payment-method-select">
                <label htmlFor="customer-select">Cliente (Opcional)</label>
                <select
                  id="customer-select"
                  value={selectedCustomerId || ''}
                  onChange={(e) => setSelectedCustomerId(e.target.value ? Number(e.target.value) : undefined)}
                  className="payment-select"
                >
                  <option value="">Cliente General</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="payment-method-select">
                <label htmlFor="payment-method">Método de Pago</label>
                <select
                  id="payment-method"
                  value={selectedPaymentMethod}
                  onChange={(e) => setSelectedPaymentMethod(Number(e.target.value))}
                  className="payment-select"
                >
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedPaymentMethod === 3 && (
                <div className="transfer-details">
                  <input
                    type="text"
                    placeholder="Nombre del Banco"
                    className="payment-input"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="# Referencia / Comprobante"
                    className="payment-input"
                    value={transferReference}
                    onChange={(e) => setTransferReference(e.target.value)}
                  />
                </div>
              )}

              <div className="total-section">
                <div className="total-label">Total</div>
                <div className="total-amount">${total.toFixed(2)}</div>
              </div>

              <button
                className="complete-sale-btn"
                onClick={completeSale}
                disabled={processing}
              >
                <DollarSign size={24} />
                {processing ? 'Procesando...' : 'Completar Venta'}
              </button>
            </div>
          )}
        </div>

        {/* Success Modal */}
        {showSuccess && (
          <div className="success-overlay">
            <div className="success-modal">
              <CheckCircle size={64} className="success-icon" />
              <h2>¡Venta Completada!</h2>
              <p>Venta #{lastSaleId} procesada exitosamente</p>
              <div className="success-amount">${total.toFixed(2)}</div>
            </div>
          </div>
        )}
      </div>
  );
};

export default Sales;
